import { BaseButton, IconButton, Stack, TooltipDelay, TooltipHost } from "office-ui-fabric-react";
import { IDesk, ToolType } from "../../../types/Floorplan.types";

export interface IRoomEditorToolsProps {
    desks: IDesk[];
    undoStates: any;
    redoStates: any;
    editableSelection: boolean;
    removableSelection: boolean;
    selectedTool: string;
    onFitToScreen: () => void;
    onSettingToggle: (boolName: string) => React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | HTMLSpanElement> | undefined;
    onToolSelection: (toolName: ToolType) => React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | HTMLSpanElement> | undefined;
    onEdit: () => void;
    onUndo: () => void;
    onRedo: () => void;
    onDelete: () => void;
}

const iconStyle = (name: string, selectedTool: string): React.CSSProperties => {
    let background: string | undefined = undefined;
    if (name === selectedTool) background = "#cfd8dc";
    return {
        fontSize: 20, background
    };
};

const RoomEditorTools = (props: IRoomEditorToolsProps) => {
    const titleStyle = { root: {
            paddingLeft: 10,
            paddingRight: 10,
            borderRight: "1px solid #e3e3e3",
            "> div": {
                textTransform: "uppercase",
                fontSize: "0.7rem"
            },
            "> div:first-letter": {
                fontSize: "0.8rem"
            }
        }
    };
    return (
        <Stack horizontal verticalAlign="center">
            <Stack styles={titleStyle}>
                <div>Tools</div>
                <Stack horizontal verticalAlign="center">
                    <TooltipHost content="Pointer">
                        <IconButton 
                            iconProps={{iconName: "pointer-svg"}} 
                            onClick={props.onToolSelection("Pointer")} 
                            style={iconStyle("Pointer", props.selectedTool)}
                        />
                    </TooltipHost>
                    <TooltipHost content="Pen">
                        <IconButton
                            iconProps={{ iconName: "pen-svg" }}
                            onClick={props.onToolSelection("Pen")}
                            style={iconStyle("Pen", props.selectedTool)}
                        />
                    </TooltipHost>
                    <TooltipHost 
                        delay={TooltipDelay.long} 
                        content={
                            <span>
                                <strong>Desk</strong><br/><br/>
                                Toggle between bookable and non-bookable with the letter T.<br/><br/>
                                Rotate with the letter R.
                            </span>
                        }>
                        <IconButton 
                            iconProps={{iconName: "desk-svg"}}
                            onClick={props.onToolSelection("Desk")} 
                            style={iconStyle("Desk", props.selectedTool)}
                        />
                    </TooltipHost>
                    <TooltipHost content="Text">
                        <IconButton 
                            iconProps={{iconName: "TextField"}} 
                            onClick={props.onToolSelection("Text")}
                            style={iconStyle("Text", props.selectedTool)} 
                        />
                    </TooltipHost>
                    <TooltipHost 
                        content={
                            <span>
                                <strong>Floor Space</strong><br/><br/>
                                Toggle between the floor space and internal room tools with the letter I.
                            </span>
                        }>
                        <IconButton 
                            iconProps={{iconName: "SingleColumnEdit"}} 
                            onClick={props.onToolSelection("Floor")} 
                            style={iconStyle("Floor", props.selectedTool)}
                        />
                    </TooltipHost>
                    <TooltipHost 
                        content={
                            <span>
                                <strong>Internal Room / Rectangle</strong><br/><br/>
                                Toggle between the floor space and internal room tools with the letter I.
                            </span>
                        }>
                        <IconButton 
                            iconProps={{iconName: "FiveTileGrid"}} 
                            onClick={props.onToolSelection("Room")} 
                            style={iconStyle("Room", props.selectedTool)}
                        />
                    </TooltipHost>
                    <TooltipHost content="Door">
                        <IconButton
                            iconProps={{iconName: "Door"}}
                            onClick={props.onToolSelection("Door")}
                            style={iconStyle("Door", props.selectedTool)}
                            />
                    </TooltipHost>
                </Stack>    
            </Stack>
            <Stack styles={titleStyle}>
                <div>View</div>
                <Stack horizontal verticalAlign="center">
                    <TooltipHost content="Show/Hide Grid">
                        <IconButton 
                            iconProps={{iconName: "GridViewSmall"}}
                            onClick={props.onSettingToggle("showGrid")} 
                            style={{fontSize: 20}}
                        />
                    </TooltipHost>
                    <TooltipHost content="Snap to Grid (S)">
                        <IconButton 
                            iconProps={{iconName: "SnapToGrid"}} 
                            onClick={props.onSettingToggle("snapToGrid")} 
                            style={{fontSize: 20}}
                        />
                    </TooltipHost>
                    <TooltipHost content="Fit Desks to Screen">
                        <IconButton 
                            disabled={props.desks.length === 0} 
                            iconProps={{iconName: "ScaleUp"}} 
                            onClick={props.onFitToScreen} 
                            style={{fontSize: 20}}
                        />
                    </TooltipHost>
                </Stack>    
            </Stack>
            <Stack styles={titleStyle}>
                <div>Editing</div>
                <Stack horizontal verticalAlign="center">
                    <TooltipHost content="Select">
                        <IconButton 
                            iconProps={{iconName: "BorderDot"}}
                            onClick={props.onToolSelection("Select")} 
                            style={iconStyle("Select", props.selectedTool)}
                        />
                    </TooltipHost>
                    <TooltipHost content="Edit">
                        <IconButton 
                            disabled={!props.editableSelection} 
                            iconProps={{iconName: "Edit"}}
                            onClick={() => { props.onEdit(); }} 
                            style={{fontSize: 20}}
                        />
                    </TooltipHost>
                    <TooltipHost content="Delete">
                        <IconButton 
                            disabled={!props.removableSelection} 
                            iconProps={{iconName: "Delete"}}
                            onClick={() => { props.onDelete(); }} 
                            style={{fontSize: 20}}
                        />
                    </TooltipHost>
                </Stack>
            </Stack>
            <Stack styles={titleStyle}>
                <div>Other</div>
                <Stack horizontal verticalAlign="center">
                    <TooltipHost content="Undo (Ctrl + Z)">
                        <IconButton 
                            disabled={props.undoStates.length === 0} 
                            iconProps={{iconName: "Undo"}} 
                            onClick={() => { props.onUndo(); }} 
                            style={{fontSize: 20}}
                        />
                    </TooltipHost>
                    <TooltipHost content="Redo (Ctrl + Y)">
                        <IconButton 
                            disabled={props.redoStates.length === 0} 
                            iconProps={{iconName: "Redo"}}
                            onClick={() => { props.onRedo(); }} 
                            style={{fontSize: 20}}
                        />
                    </TooltipHost>
                </Stack>    
            </Stack>
        </Stack>
    );
};

export default RoomEditorTools;